import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTimeIntervals } from '../../../services/timeIntervals';
import { useEvent } from '../../../../../hooks/useEvent';

const IdsContext = createContext();
const CanAddContext = createContext();
const ActionsContext = createContext();

export const useSelectedIds = () => useContext(IdsContext);
export const useCanAddInterval = () => useContext(CanAddContext);
export const useSelectedIdsActions = () => useContext(ActionsContext);

export const SelectedIdsProvider = ({ children, initialIds }) => {
  const [value, setValue] = useState(() => new Set(initialIds));

  const timeIntervals = useTimeIntervals();

  const onChange = useEvent((prevId, currentId) => {
    setValue((prev) => {
      if (prevId === currentId) return prev;

      let hasChange = false;

      if (prevId) {
        hasChange = true;
        prev.delete(prevId);
      }

      if (currentId) {
        hasChange = true;
        prev.add(currentId);
      }

      return hasChange ? new Set(prev) : prev;
    });
  });

  const deleteId = useEvent((id) => {
    setValue((prev) => {
      prev.delete(id);
      return new Set(prev);
    });
  });

  useEffect(() => {
    if (Array.isArray(initialIds) || initialIds instanceof Set) {
      setValue((prev) => {
        let next = prev;

        for (const id of initialIds) {
          if (!next.has(id)) {
            if (next === prev) {
              next = new Set(prev);
            }
            next.add(id);
          }
        }

        return next;
      });
    }
  }, [initialIds]);

  const getSelectedIds = useEvent(() => {
    return value;
  });

  const canAdd = value.size < timeIntervals.size;

  const actions = useState(() => ({ getSelectedIds, onChange, deleteId }))[0];

  return (
    <IdsContext.Provider value={value}>
      <CanAddContext.Provider value={canAdd}>
        <ActionsContext.Provider value={actions}>
          {children}
        </ActionsContext.Provider>
      </CanAddContext.Provider>
    </IdsContext.Provider>
  );
};
